import React, { useEffect, useState, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import PrayerForm from './PrayerForm';
import { PRAYER_CATEGORIES } from '../constants';

const PAGE_SIZE = 10;

function PrayerCard({ prayer, onClick }) {
  const bgColor = prayer.isAnswered ? '#f0fdf4' : '#fff';
  const borderColor = prayer.isAnswered ? '#86efac' : '#ccc';
  
  // Determine display name
  let displayName = prayer.name || 'Anonymous';
  if (prayer.isAnnonymous && prayer.userId && window.currentUserUid && prayer.userId !== window.currentUserUid) {
    displayName = 'Anonymous';
  }
  return (
    <div
      className="prayer-card"
      onClick={onClick}
      style={{border: `1px solid ${borderColor}`, borderRadius: 8, padding: 16, marginBottom: 16, background: bgColor, cursor: 'pointer'}}
    >
      <div style={{fontWeight: 'bold'}}>{displayName}</div>
      <div style={{margin: '8px 0'}}>{prayer.description}</div>
      <div style={{fontSize: 12, color: '#666'}}>Created: {prayer.createdAt ? new Date(prayer.createdAt.seconds * 1000).toLocaleString() : 'Unknown'}</div>
      <div style={{fontSize: 12, color: '#666'}}>Categories: {prayer.category ? prayer.category.join(', ') : 'None'}</div>
    </div>
  );
}

const PrayerCards = ({ user, refreshTrigger }) => {
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const lastDocRef = useRef(null);
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchPrayers = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && (loading || !hasMore)) return;
    setLoading(true);
    let q = query(
      collection(db, 'prayers'),
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE)
    );
    if (!forceRefresh && lastDocRef.current) {
      q = query(
        collection(db, 'prayers'),
        orderBy('createdAt', 'desc'),
        startAfter(lastDocRef.current),
        limit(PAGE_SIZE)
      );
    }
    const snapshot = await getDocs(q);
    const newPrayers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    if (forceRefresh) {
      // For refresh, replace all prayers
      setPrayers(newPrayers);
    } else {
      // For pagination, avoid adding duplicates
      setPrayers(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const filtered = newPrayers.filter(p => !existingIds.has(p.id));
        return [...prev, ...filtered];
      });
    }
    
    if (snapshot.docs.length < PAGE_SIZE) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
    if (snapshot.docs.length > 0) {
      lastDocRef.current = snapshot.docs[snapshot.docs.length - 1];
    }
    setLoading(false);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchPrayers(true);
    // eslint-disable-next-line
  }, []);

  // Reset and refresh when a new prayer is added
  useEffect(() => {
    if (refreshTrigger > 0) {
      lastDocRef.current = null;
      fetchPrayers(true);
    }
    // eslint-disable-next-line
  }, [refreshTrigger]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 200 && hasMore && !loading
      ) {
        fetchPrayers();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchPrayers, hasMore, loading]);

  // Make user.uid available globally for PrayerCard
  useEffect(() => {
    window.currentUserUid = user && user.uid ? user.uid : null;
    return () => { window.currentUserUid = null; };
  }, [user]);

  // Filter prayers based on selected category
  const filteredPrayers = selectedCategory === 'all' 
    ? prayers 
    : prayers.filter(prayer => 
        prayer.category && prayer.category.includes(selectedCategory)
      );

  return (
    <div style={{maxWidth: 600, margin: '32px auto'}}>
      {/* Category Filter Dropdown */}
      <div style={{marginBottom: 24}}>
        <label style={{fontWeight: 'bold', marginRight: 12}}>Filter by Category:</label>
        <select 
          value={selectedCategory} 
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: 4,
            border: '1px solid #ccc',
            fontSize: 14,
            cursor: 'pointer'
          }}
        >
          <option value="all">All Categories</option>
          {PRAYER_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {filteredPrayers.map(prayer => (
        <PrayerCard key={prayer.id} prayer={prayer} onClick={() => setSelectedPrayer(prayer)} />
      ))}
      {loading && (
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem'}}>
          <div className="loader" style={{marginBottom: '1rem'}} aria-hidden="true" />
          <div style={{color: '#666'}}>Loading...</div>
        </div>
      )}
      {!hasMore && filteredPrayers.length > 0 && <div style={{textAlign: 'center', color: '#888'}}>No more prayers to show.</div>}
      {!loading && filteredPrayers.length === 0 && prayers.length > 0 && (
        <div style={{textAlign: 'center', color: '#888', marginTop: 20}}>
          No prayers found in this category.
        </div>
      )}
      {!loading && prayers.length === 0 && (
        <div style={{textAlign: 'center', color: '#888', marginTop: 20}}>
          No prayers yet. Be the first to add one!
        </div>
      )}

      <PrayerForm
        isOpen={!!selectedPrayer}
        prayer={selectedPrayer}
        user={user}
        onClose={() => setSelectedPrayer(null)}
        onSaved={(updated) => {
          setPrayers((prev) => prev.map(p => (p.id === updated.id ? updated : p)));
        }}
        onDeleted={(deletedId) => {
          setPrayers((prev) => prev.filter(p => p.id !== deletedId));
        }}
      />
    </div>
  );
};

export default PrayerCards;
